import React, { useState, useEffect } from 'react';
import { FaTimes, FaBuilding, FaSave, FaSpinner, FaInfoCircle, FaUser } from 'react-icons/fa';

interface Client {
    id: number;
    client_full_name: string;
    client_short_name: string;
    is_active: boolean;
}

interface Project {
    id: number;
    project_name: string;
    status: string;
    created_at: string;
}

interface ClientProject {
    id: number;
    client_id: number;
    client_project_name: string;
    client_project_description?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    created_by: number;
    suspended_by?: number;
    suspended_at?: string;
    client?: Client;
    projects?: Project[];
    project_count?: number;
}

interface ClientProjectFormData {
    client_id: number;
    client_project_name: string;
    client_project_description: string;
    status?: 'ACTIVE' | 'SUSPENDED';
}

interface EditClientProjectFormData extends ClientProjectFormData {
    id: number;
}

// Format date
export const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

interface EditClientProjectOffcanvasProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: EditClientProjectFormData) => void;
    clientProject: ClientProject | null;
    clients: Client[];
    loading?: boolean;
}

export const EditClientProjectOffcanvas: React.FC<EditClientProjectOffcanvasProps> = ({
    isOpen,
    onClose,
    onSubmit,
    clientProject,
    clients,
    loading = false
}) => {
    const [formData, setFormData] = useState<ClientProjectFormData>({
        client_id: 0,
        client_project_name: '',
        client_project_description: '',
        status: 'ACTIVE'
    });

    const [errors, setErrors] = useState<Partial<ClientProjectFormData>>({});
    const [touched, setTouched] = useState<Partial<Record<keyof ClientProjectFormData, boolean>>>({});

    // Populate form when clientProject changes
    useEffect(() => {
        if (clientProject && isOpen) {
            setFormData({
                client_id: clientProject.client_id || 0,
                client_project_name: clientProject.client_project_name || '',
                client_project_description: clientProject.client_project_description || '',
                status: clientProject.is_active ? 'ACTIVE' : 'SUSPENDED'
            });
            setErrors({});
            setTouched({});
        }
    }, [clientProject, isOpen]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setErrors({});
            setTouched({});
        }
    }, [isOpen]);

    // Validation
    const validateField = (name: keyof ClientProjectFormData, value: string | number) => {
        switch (name) {
            case 'client_id':
                if (!value || value === 0) {
                    return 'Please select a client';
                }
                return '';
            case 'client_project_name':
                if (!value || !String(value).trim()) {
                    return 'Client project name is required';
                }
                if (String(value).trim().length < 3) {
                    return 'Client project name must be at least 3 characters';
                }
                if (String(value).trim().length > 200) {
                    return 'Client project name cannot exceed 200 characters';
                }
                return '';
            case 'client_project_description':
                if (String(value).trim().length > 1000) {
                    return 'Description cannot exceed 1000 characters';
                }
                return '';
            default:
                return '';
        }
    };

    const validateForm = (): boolean => {
        const newErrors: any = {};
        let isValid = true;

        // Validate all fields
        Object.keys(formData).forEach((key) => {
            const fieldKey = key as keyof ClientProjectFormData;
            const error = validateField(fieldKey, formData[fieldKey] as any);
            if (error) {
                newErrors[fieldKey] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        const fieldName = name as keyof ClientProjectFormData;

        // Special handling for client_id and status
        let fieldValue: any = value;
        if (fieldName === 'client_id') {
            fieldValue = Number(value);
        }

        setFormData(prev => ({
            ...prev,
            [fieldName]: fieldValue
        }));

        // Clear error when user starts typing
        if (errors[fieldName]) {
            setErrors(prev => ({
                ...prev,
                [fieldName]: ''
            }));
        }

        // Mark field as touched
        setTouched(prev => ({
            ...prev,
            [fieldName]: true
        }));
    };

    // Handle blur events for validation
    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        const fieldName = name as keyof ClientProjectFormData;

        setTouched(prev => ({
            ...prev,
            [fieldName]: true
        }));

        const fieldValue = fieldName === 'client_id' ? Number(value) : value;
        const error = validateField(fieldName, fieldValue);
        setErrors(prev => ({
            ...prev,
            [fieldName]: error
        }));
    };

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!clientProject) return;

        // Mark all fields as touched
        setTouched({
            client_id: true,
            client_project_name: true,
            client_project_description: true,
            status: true
        });

        if (validateForm()) {
            onSubmit({
                ...formData,
                id: clientProject.id
            });
        }
    };

    // Check if form has changes
    const hasChanges = clientProject ? (
        formData.client_id !== clientProject.client_id ||
        formData.client_project_name !== clientProject.client_project_name ||
        formData.client_project_description !== (clientProject.client_project_description || '') ||
        formData.status !== (clientProject.is_active ? 'ACTIVE' : 'SUSPENDED')
    ) : false;

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen && !loading) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, loading, onClose]);

    if (!isOpen || !clientProject) return null;

    const activeProjects = clientProject.projects?.filter(p => p.status === 'ACTIVE').length || 0;
    const canSuspend = activeProjects === 0;
    const selectedClient = clients.find(client => client.id === formData.client_id);

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
                onClick={!loading ? onClose : undefined}
            />

            {/* Offcanvas */}
            <div className="overflow-y-auto fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-800">Edit Client Project</h2>
                        <p className="text-sm text-gray-600 mt-1">Last updated: {formatDate(clientProject.updated_at)}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        disabled={loading}
                    >
                        <FaTimes className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto p-6">
                        {/* Client Project Info */}
                        <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
                            <h4 className="text-sm font-medium text-gray-800 mb-2">Project Information</h4>
                            <div className="grid grid-cols-2 gap-4 text-xs">
                                <div>
                                    <span className="text-gray-500">Created:</span>
                                    <p className="font-medium">{formatDate(clientProject.created_at)}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500">Last Updated:</span>
                                    <p className="font-medium">{formatDate(clientProject.updated_at)}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500">Projects:</span>
                                    <p className="font-medium">{clientProject.project_count || 0}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500">Active Projects:</span>
                                    <p className="font-medium">{activeProjects}</p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Client Selection */}
                            <div>
                                <label htmlFor="client_id" className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Client *
                                </label>
                                <div className="relative">
                                    <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                    <select
                                        id="client_id"
                                        name="client_id"
                                        value={formData.client_id}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        disabled={loading}
                                        className={`w-full pl-10 pr-3 py-3 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white ${
                                            errors.client_id && touched.client_id
                                                ? 'border-red-300 bg-red-50'
                                                : 'border-gray-300'
                                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                                    >
                                        <option value={0}>Choose a client...</option>
                                        {clients.map((client) => (
                                            <option key={client.id} value={client.id}>
                                                {client.client_full_name} ({client.client_short_name})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {errors.client_id && touched.client_id && (
                                    <p className="mt-1 text-sm text-red-600">{errors.client_id}</p>
                                )}
                                {selectedClient && (
                                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                                        <p className="text-sm text-blue-800">
                                            <span className="font-medium">Selected:</span> {selectedClient.client_full_name}
                                        </p>
                                        <p className="text-xs text-blue-600 mt-1">
                                            Short Name: {selectedClient.client_short_name}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Client Project Name */}
                            <div>
                                <label htmlFor="client_project_name" className="block text-sm font-medium text-gray-700 mb-2">
                                    Client Project Name *
                                </label>
                                <input
                                    type="text"
                                    id="client_project_name"
                                    name="client_project_name"
                                    value={formData.client_project_name}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    disabled={loading}
                                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                        errors.client_project_name && touched.client_project_name
                                            ? 'border-red-300 bg-red-50'
                                            : 'border-gray-300'
                                    } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                                    placeholder="Enter client project name"
                                    maxLength={200}
                                />
                                {errors.client_project_name && touched.client_project_name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.client_project_name}</p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">
                                    {formData.client_project_name.length}/200 characters
                                </p>
                            </div>

                            {/* Client Project Description */}
                            <div>
                                <label htmlFor="client_project_description" className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    id="client_project_description"
                                    name="client_project_description"
                                    value={formData.client_project_description}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    disabled={loading}
                                    rows={4}
                                    className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${
                                        errors.client_project_description && touched.client_project_description
                                            ? 'border-red-300 bg-red-50'
                                            : 'border-gray-300'
                                    } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                                    placeholder="Enter project description (optional)"
                                    maxLength={1000}
                                />
                                {errors.client_project_description && touched.client_project_description && (
                                    <p className="mt-1 text-sm text-red-600">{errors.client_project_description}</p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">
                                    {formData.client_project_description.length}/1000 characters
                                </p>
                            </div>

                            {/* Status */}
                            <div>
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                                    Status
                                </label>
                                <select
                                    id="status"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    disabled={loading || (!canSuspend && formData.status === 'ACTIVE')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                                >
                                    <option value="ACTIVE">Active</option>
                                    <option value="SUSPENDED">Suspended</option>
                                </select>

                                {!canSuspend && formData.status === 'ACTIVE' && (
                                    <div className="mt-2 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                                        <FaInfoCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-amber-800">
                                            <p className="font-medium">Cannot suspend client project</p>
                                            <p className="text-xs mt-1">This client project has {activeProjects} active project(s). Suspend or complete all active projects before suspending the client project.</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Projects List (if any) */}
                            {clientProject.projects && clientProject.projects.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Associated Projects</h4>
                                    <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-32 overflow-y-auto">
                                        {clientProject.projects.map((project) => (
                                            <div key={project.id} className="flex justify-between items-center py-1">
                                                <span className="text-sm text-gray-900 truncate">{project.project_name}</span>
                                                <span className={`text-xs px-2 py-1 rounded-full ${
                                                    project.status === 'ACTIVE'
                                                        ? 'bg-green-100 text-green-800'
                                                        : project.status === 'SUSPENDED'
                                                        ? 'bg-red-100 text-red-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {project.status}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>

                    {/* Footer */}
                    <div className="border-t bg-gray-50 px-6 py-4">
                        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={loading || !hasChanges || !formData.client_project_name.trim() || !formData.client_id}
                                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <FaSpinner className="w-4 h-4 animate-spin" />
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <FaSave className="w-4 h-4" />
                                        Update Client Project
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};